import { get } from 'lodash'
// import moment from 'moment'
import { DateUtil } from '@utils'
import { MouseEvent } from 'types'
import React, { ReactNode } from 'react'
import { Checkbox, Menu, MenuItem, MenuDivider, Position, Tag } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { Flex, Icon } from '@ui'
import { IBuildingDetails, IFloorDetails, IPortfolioRegion, IPortfolioCity } from '@api'
import { Mode } from './types'
import { useLocationListScroll } from './useLocationListScroll'
import css from './locationSelect.module.scss'

export function CityColumn({
  multi,
  regions,
  cityNav,
  setCityNav,
  onSelect,
  isSelected,
}: {
  multi?: boolean
  regions: IPortfolioRegion[]
  cityNav: IPortfolioCity | null
  setCityNav: (city: IPortfolioCity | null) => void
  onSelect?: (item: IPortfolioRegion | IPortfolioCity) => void
  isSelected?: (item: IPortfolioRegion | IPortfolioCity) => boolean
}) {
  const listRef = useLocationListScroll(cityNav, regions)

  return (
    <div className={css.col}>
      <div className={css.col_header}>Locations</div>
      <div className={css.list} id="list-scroll" ref={listRef}>
        <Menu>
          {regions.reduce((result: ReactNode[], region: IPortfolioRegion) => {
            return [
              ...result,
              multi ? (
                <MenuItem
                  key={region.name}
                  shouldDismissPopover={false}
                  onClick={(e: MouseEvent) => {
                    e.preventDefault()
                    onSelect?.(region)
                  }}
                  text={
                    <Flex align="baseline">
                      {multi && <Checkbox checked={isSelected?.(region) || false} />}
                      {region.name}
                    </Flex>
                  }
                />
              ) : (
                <MenuDivider key={region.name} title={region.name} />
              ),
              ...region.cities.map((city) => (
                <MenuItem
                  key={city.id}
                  shouldDismissPopover={false}
                  active={city === cityNav}
                  text={
                    <City
                      city={city}
                      multi={multi}
                      selected={isSelected?.(region) || isSelected?.(city) || false}
                    />
                  }
                  onClick={(e: MouseEvent) => {
                    e.preventDefault()
                    onSelect?.(city)
                    setCityNav(city)
                  }}
                  data-item={city.name}
                />
              )),
            ]
          }, [])}
        </Menu>
      </div>
    </div>
  )
}

export function City({
  city,
  multi,
  selected,
  onSelect,
}: {
  city: IPortfolioCity
  multi?: boolean
  selected: boolean
  onSelect?: (city: IPortfolioCity) => void
}) {
  const state = get(city, 'buildings[0].state')

  return (
    <Flex align="baseline" style={{ marginLeft: multi ? 26 : 0 }}>
      {multi && (
        <Checkbox
          checked={selected}
          onChange={() => {
            onSelect?.(city)
          }}
        />
      )}
      {city.name}
      {state && (
        <div className={css.small} style={{ fontSize: 12, marginLeft: 5 }}>
          {state}
        </div>
      )}
    </Flex>
  )
}

export function Building({
  mode,
  building,
  selected,
}: {
  mode: Mode
  building: IBuildingDetails
  selected: boolean
}) {
  const renderCheckbox = mode === Mode.MULTI_BUILDING || mode === Mode.MULTI_FLOOR
  let old: boolean | undefined
  if (building.closedAt) old = DateUtil().isAfter(building.closedAt)

  return (
    <Flex>
      {renderCheckbox && (
        <Checkbox checked={selected} disabled={!building.floors?.length} onChange={() => null} />
      )}
      <div>
        <div className={css.normal} style={{ fontWeight: 'bolder' }}>
          {building.name}{' '}
          {old && (
            <Tooltip2 content="Expired Building">
              <Tag minimal round style={{ fontWeight: 'normal' }}>
                Exp
              </Tag>
            </Tooltip2>
          )}
        </div>
        {building.address && (
          <div className={css.small} style={{ fontSize: 12 }}>
            {building.address}
          </div>
        )}
      </div>
    </Flex>
  )
}

export function Floor({
  mode,
  floor,
  selected,
}: {
  mode: Mode
  floor: IFloorDetails
  selected: boolean
}) {
  return (
    <Flex justify="space-between">
      <Flex align="center">
        {mode === Mode.MULTI_FLOOR && (
          <Checkbox onChange={() => null} checked={selected} style={{ marginLeft: 26 }} />
        )}
        <div>{floor.name}</div>
      </Flex>
      {floor.hidden && (
        <Tooltip2 content="This floor is hidden" position={Position.TOP}>
          <div>
            <Icon name="eye-slash" style={{ color: 'orange', marginLeft: 8 }} />
          </div>
        </Tooltip2>
      )}
    </Flex>
  )
}
